import React from "react";
import Config from "../../config/main";
import _, { split } from "lodash";
import { t } from "../translator";
import moment from "moment";
import { createSelector } from "reselect";
import { niceEventName } from "./eventNames";
import { BetslipSetSystemOpt, GetSuperBetInfo } from "../../actions/betslip";
import { OpenPopup } from "../../actions/ui";
import {
	MAKE_QUICK_BET,
	BETSLIP_TOGGLE_EVENT,
	BETSLIP_ADD_COMPARE_EVENT,
	SWORM_DATA_BETSLIP_ADD_EVENT,
	BETSLIP_TOGGLE_HORSE_EVENT,
} from "../../actions/actionTypes";
import { mathCuttingFunction } from "../filters";
import Helpers from "../helperFunctions";
import OddConverter from "@common/helpers/oddConverter";
import { BetPriceChangeEnum } from "../../constants/enums/betPriceChange";

export const BETSLIP_TYPE_SINGLE = 1;
export const BETSLIP_TYPE_EXPRESS = 2;
export const BETSLIP_TYPE_SYSTEM = 3;
export const BETSLIP_TYPE_CHAIN = 4;

export const BetslipTypes = {
	[BETSLIP_TYPE_SINGLE]: "Single",
	[BETSLIP_TYPE_EXPRESS]: "Express",
	[BETSLIP_TYPE_SYSTEM]: "System",
};

if (Config.betting.enableChainBet) BetslipTypes[BETSLIP_TYPE_CHAIN] = "Chain";

export const BetslipTypesUSA = {
	[BETSLIP_TYPE_SINGLE]: "Straight",
	[BETSLIP_TYPE_EXPRESS]: "Parlay",
	[BETSLIP_TYPE_SYSTEM]: "Round Robin",
};

if (Config.betting && Config.betting.disableBetslipTypes) {
	for (let i in Config.betting.disableBetslipTypes) {
		delete BetslipTypes[Config.betting.disableBetslipTypes[i]];
	}
}
/**
 * Returns single bet object to be put in store
 * @param {Object} game game object
 * @param {Object} market market object
 * @param {Object} event event object
 * @param {String} oddType odd type, "odd" or "sp"
 * @param {Number} order
 * @returns {Object}
 */
export const createBetslipEventObject = (game, market, event, oddType = "odd", isBoosted, order) => {
	return {
		oddType,
		gameId: game.id,
		sportAlias: game.alias,
		gameType: game.type,
		blocked:
			game.is_blocked ||
			event.price === 1 ||
			(Config.main.blockedPrices &&
				Config.main.blockedPrices.minPrice &&
				Config.main.blockedPrices.maxPrice &&
				event.price >= Config.main.blockedPrices.minPrice &&
				event.price <= Config.main.blockedPrices.maxPrice),
		marketName: market.name,
		marketType: market.type,
		marketOriginalType: market.market_type,
		competitionId: game.competitionId,
		regionId: game.regionId,
		conflicts: [],
		marketDisplayKey: market.display_key,
		marketId: market.id,
		homeScore: market.home_score,
		awayScore: market.away_score,
		expressId: market.express_id,
		excludeIds: game.exclude_ids,
		eventId: event.id,
		eventType: event.type,
		ewAllowed: !!event.ew_allowed, //each way
		expMinLen: game.express_min_len,
		isLive: !!game.is_live || game.type === 1,
		isBoosted: isBoosted,
		eachWay: false,
		incInSysCalc: true,
		singleStake: "",
		startTime: moment.unix(game.start_ts).format("MM-DD-YYYY, H:mm"),
		singleUnitStake: "", // each way
		initialPrice: event.price,
		price: event.price,
		initialBase: event.base || market.base,
		eventBases:
			event.base1 !== undefined && event.base2 !== undefined ? [event.base1, event.base2] : undefined,
		title:
			game.team1_name && game.team2_name
				? `${game.team1_name} ${game.team2_name ? "-" + game.team2_name : ""}`
				: `${game.team1_name}`,
		team1_name: game.team1_name || "",
		team2_name: game.team2_name || "",
		pick: niceEventName(event.name, game),
		processing: false,
		order: order,
	};
};

/**
 * Checks if  "bet" has conflict with one of bets "allBets" (has same express_id in same game or exists in exclude_ids)
 * @param {Object} allBets betslip bets
 * @param {Object} bet single bet
 * @returns {Array} conflicts
 */
export function geBetConflicts(allBets, bet) {
	var conflicts = [];

	Object.keys(allBets).map(id => {
		let b = allBets[id];
		if (parseInt(id, 10) === bet.eventId) {
			// can't have conflict with itself
			return;
		}
		if (
			(b.gameId === bet.gameId && (bet.expressId === undefined || b.expressId === bet.expressId)) ||
			b.gameId === bet.excludeIds ||
			b.excludeIds === bet.gameId
		) {
			conflicts.push(b.eventId);
		}
	});
	return conflicts;
}

/**
 * Checks if bets in a list are conflicting with each other and sets "conflicts" field of corresponding bets
 * @param {Object} allBets
 */
export function detectConflicts(allBets) {
	Object.keys(allBets).map(id => {
		allBets[id].conflicts = geBetConflicts(allBets, allBets[id]) || [];
	});
}

/**
 * Calculates factorial
 * @param {Number} x
 * @returns {number}
 */
function factorial(x) {
	if (x !== undefined && !isNaN(x) && x >= 0) {
		return x === 0 ? 1 : x * factorial(x - 1);
	}
}

/**
 * Merges data from swarm with data from store and returns betslip events and info
 * @param swarmEventData data from swarm
 * @param storeBetSlipData data from store
 * @param multipleOptions ukBetslip options
 * @param minBetAmount minimum bet amount for specified currency
 * @returns {{events: Array, info: {}}}
 */
export function getBetslipData(swarmEventData, storeBetSlipData, multipleOptions, minBetAmount = 0, partnerConfig = 0) {
	let events = [],
		info = {},
		sameGameIds = {},
		totalStake = 0,
		multipleBetsStake = 0,
		enableMinStakeValidation = storeBetSlipData.counterOffer && minBetAmount,
		minUnitStake = minBetAmount;

	if (!_.isEmpty(storeBetSlipData?.events)) {
		multipleOptions &&
			Object.keys(multipleOptions).map(key => {
				if (key !== "Singles" && key !== "enabled") {
					totalStake += 1 * multipleOptions[key].value * multipleOptions[key].quantity;
					multipleBetsStake += totalStake;
					//info.hasWrongStakes = info.hasWrongStakes || (!isNaN(multipleOptions[key].value) && multipleOptions[key].value !== "" && (multipleOptions[key].value * 1) < minBetAmount);
					//TODO discuss with QA
					info.hasWrongStakes =
						info.hasWrongStakes ||
						(!isNaN(multipleOptions[key].value) &&
							multipleOptions[key].value !== "" &&
							1 * multipleOptions[key].value * multipleOptions[key].quantity <
								minBetAmount);
				}
			});

		const eventsArr = Object.values(storeBetSlipData.events);

		if (storeBetSlipData.type === BETSLIP_TYPE_CHAIN)
			eventsArr.sort((a, b) => a.__originalAddedOrder - b.__originalAddedOrder);
		else if (Config.enableOrderInBetslip) eventsArr.sort((a, b) => a.order - b.order);

		eventsArr.map(bet => {
			const { eventId } = bet;
			if (bet.singleStake) {
				totalStake += 1 * bet.singleStake;
			}
			if (swarmEventData?.event[eventId]) {
				bet.deleted = false;
				bet.available = true;

				if (bet.price && bet.price !== swarmEventData.event[eventId].price)
					bet.initialPrice = bet.price;

				bet.price = swarmEventData.event[eventId].price;
				const maxOddToOffer = partnerConfig.odds_raised_percent
					? (bet.price / 100) * (100 + partnerConfig.odds_raised_percent)
					: bet.price;

				const splittedOdd = `${maxOddToOffer}`.split(".");
				if (splittedOdd[1]) splittedOdd[1] = splittedOdd[1].substring(0, 2);
				bet.maxOddToOffer = +splittedOdd.join(".");
				bet.base = swarmEventData.event[eventId].base;
				bet.betterOdd = calcBetterOdd(bet.price);
				bet.blocked = swarmEventData.event[eventId].blocked > 0;
				bet.horseType = storeBetSlipData.horseEvents[eventId]?.type;
				info.priceChange =
					info.priceChange || bet.initialPrice !== swarmEventData.event[eventId].price;
				info.priceWentDown =
					info.priceWentDown || bet.initialPrice < swarmEventData.event[eventId].price;
				info.priceWentUp =
					info.priceWentUp || bet.initialPrice > swarmEventData.event[eventId].price;
				info.baseChange =
					info.baseChange || bet.initialBase !== swarmEventData.event[eventId].base;
				info.hasEventsFromSameGame = sameGameIds[bet.gameId]
					? true
					: info.hasEventsFromSameGame;
				sameGameIds[bet.gameId] = true;
				info.hasEachWayReadyEvents = bet.ewAllowed ? true : info.hasEachWayReadyEvents;
				info.hasSingleOnlyEvents = bet.expMinLen === 1 ? true : info.hasSingleOnlyEvents;
				info.hasLiveEvents = bet.isLive ? true : info.hasLiveEvents;
				info.hasConflicts = info.hasConflicts || bet.conflicts.length > 0;
				info.hasLockedEvents = bet.blocked ? true : info.hasLockedEvents;
				info.hasSpOddTypes = bet.oddType !== "odd" ? true : info.hasSpOddTypes;
				info.hasWrongStakes =
					(!storeBetSlipData.freeBet ||
						(storeBetSlipData.freeBet &&
							!storeBetSlipData.selectedFreeBetAmount)) &&
					(info.hasWrongStakes ||
						(multipleOptions
							? isNaN(parseFloat(bet.singleStake || multipleBetsStake)) ||
							  parseFloat(bet.singleStake || multipleBetsStake) <
									minBetAmount
							: enableMinStakeValidation
							? parseFloat(bet.singleStake) < minUnitStake
							: (isNaN(parseFloat(bet.singleStake)) &&
									bet.singleStake !== "") ||
							  parseFloat(bet.singleStake) < 0));
				info.hasEmptyStakes =
					(!storeBetSlipData.freeBet ||
						(storeBetSlipData.freeBet &&
							!storeBetSlipData.selectedFreeBetAmount)) &&
					storeBetSlipData.type === BETSLIP_TYPE_SINGLE &&
					(info.hasEmptyStakes ||
						(!multipleOptions &&
							(isNaN(parseFloat(bet.singleStake)) ||
								parseFloat(bet.singleStake) === 0)));
			} else {
				if (bet.available) bet.deleted = true;
				bet.available = false;
				bet.horseType = storeBetSlipData.horseEvents[eventId]?.type;
				info.hasDeletedEvents = true;
			}
			events.push(bet);
		});
	}
	return { events, info, totalStake };
}

/**
 * Returns <select> element with available system options based on number of bets in betslip
 * @param {Object} betslip
 * @param {Function} dispatch
 * @returns {*}
 */
export function SystemOptionsSelect(betslip, dispatch) {
	var i,
		options = [];
	let numberOfBets = (Object.keys(betslip.events) || []).length;
	for (i = 0; i < numberOfBets; i++) {
		if (options.length < numberOfBets - 2 - betslip.bankerBetsCount && i > 1) {
			options.push(
				<li key={i} onClick={e => dispatch(BetslipSetSystemOpt(e.target.value))} value={i}>
					{`${i}/${numberOfBets - betslip.bankerBetsCount}`}(
					{calculateSystemOptionsCount(i, numberOfBets)} {t("opt.")})
				</li>
			);
		}
	}
	return options.length ? <ul>{options}</ul> : null;
}

/**
 * @name calculateSystemOptionsCount
 * @methodOf betting.controller:betSlipController
 * @param {Number} k of selected events
 * @param {Number} numberOfBets number of bets in betslip
 * @param {Number} bankerBetsCount bankers bet count
 * @description calculate system options count
 */
export function calculateSystemOptionsCount(k, numberOfBets, bankerBetsCount = 0) {
	let cashedSystemOptionsCount = {
		"0_2_0": 1,
		"0_3_0": 1,
		"0_4_0": 1,
		"0_5_0": 1,
		"0_6_0": 1,
		"0_7_0": 1,
		"0_8_0": 1,
		"0_9_0": 1,
		"0_10_0": 1,
		"0_11_0": 1,
		"0_12_0": 1,
		"0_13_0": 1,
		"0_14_0": 1,
		"0_15_0": 1,
		"0_16_0": 1,
		"1_2_0": 2,
		"1_3_0": 3,
		"1_4_0": 4,
		"1_5_0": 5,
		"1_6_0": 6,
		"1_7_0": 7,
		"1_8_0": 8,
		"1_9_0": 9,
		"1_10_0": 10,
		"1_11_0": 11,
		"1_12_0": 12,
		"1_13_0": 13,
		"1_14_0": 14,
		"1_15_0": 15,
		"1_16_0": 16,
		"2_2_0": 1,
		"2_3_0": 3,
		"2_4_0": 6,
		"2_5_0": 10,
		"2_6_0": 15,
		"2_7_0": 21,
		"2_8_0": 28,
		"2_9_0": 36,
		"2_10_0": 45,
		"2_11_0": 55,
		"2_12_0": 66,
		"2_13_0": 78,
		"2_14_0": 91,
		"2_15_0": 105,
		"2_16_0": 120,
		"3_3_0": 1,
		"3_4_0": 4,
		"3_5_0": 10,
		"3_6_0": 20,
		"3_7_0": 35,
		"3_8_0": 56,
		"3_9_0": 84,
		"3_10_0": 120,
		"3_11_0": 165,
		"3_12_0": 220,
		"3_13_0": 286,
		"3_14_0": 364,
		"3_15_0": 455,
		"3_16_0": 560,
		"4_4_0": 1,
		"4_5_0": 5,
		"4_6_0": 15,
		"4_7_0": 35,
		"4_8_0": 70,
		"4_9_0": 126,
		"4_10_0": 210,
		"4_11_0": 330,
		"4_12_0": 495,
		"4_13_0": 715,
		"4_14_0": 1001,
		"4_15_0": 1365,
		"4_16_0": 1820,
		"5_5_0": 1,
		"5_6_0": 6,
		"5_7_0": 21,
		"5_8_0": 56,
		"5_9_0": 126,
		"5_10_0": 252,
		"5_11_0": 462,
		"5_12_0": 792,
		"5_13_0": 1287,
		"5_14_0": 2002,
		"5_15_0": 3003,
		"5_16_0": 4368,
		"6_6_0": 1,
		"6_7_0": 7,
		"6_8_0": 28,
		"6_9_0": 84,
		"6_10_0": 210,
		"6_11_0": 462,
		"6_12_0": 924,
		"6_13_0": 1716,
		"6_14_0": 3003,
		"6_15_0": 5005,
		"6_16_0": 8008,
		"7_7_0": 1,
		"7_8_0": 8,
		"7_9_0": 36,
		"7_10_0": 120,
		"7_11_0": 330,
		"7_12_0": 792,
		"7_13_0": 1716,
		"7_14_0": 3432,
		"7_15_0": 6435,
		"7_16_0": 11440,
		"8_8_0": 1,
		"8_9_0": 9,
		"8_10_0": 45,
		"8_11_0": 165,
		"8_12_0": 495,
		"8_13_0": 1287,
		"8_14_0": 3003,
		"8_15_0": 6435,
		"8_16_0": 12870,
		"9_9_0": 1,
		"9_10_0": 10,
		"9_11_0": 55,
		"9_12_0": 220,
		"9_13_0": 715,
		"9_14_0": 2002,
		"9_15_0": 5005,
		"9_16_0": 11440,
		"10_10_0": 1,
		"10_11_0": 11,
		"10_12_0": 66,
		"10_13_0": 286,
		"10_14_0": 1001,
		"10_15_0": 3003,
		"10_16_0": 8008,
		"11_11_0": 1,
		"11_12_0": 12,
		"11_13_0": 78,
		"11_14_0": 364,
		"11_15_0": 1365,
		"11_16_0": 4368,
		"12_12_0": 1,
		"12_13_0": 13,
		"12_14_0": 91,
		"12_15_0": 455,
		"12_16_0": 1820,
		"13_13_0": 1,
		"13_14_0": 14,
		"13_15_0": 105,
		"13_16_0": 560,
		"14_14_0": 1,
		"14_15_0": 15,
		"14_16_0": 120,
		"15_15_0": 1,
		"15_16_0": 16,
		"16_16_0": 1,
	};
	return (
		cashedSystemOptionsCount[`${k}_${numberOfBets}_${bankerBetsCount}`] ||
		Math.round(
			factorial(numberOfBets - bankerBetsCount) /
				(factorial(k) * factorial(numberOfBets - k - bankerBetsCount))
		)
	);
}

/**
 * @ngdoc method
 * @name calculateSystemPossibleWin
 * @methodOf betting.controller:betSlipController
 * @returns {Object} possible win and options count
 * @description calculate system possible winning sets system selected value
 */
export function calculateSystemPossibleWin(allBets, sysBetSelectedValue, stake, bankerBetsCount, eachWayMode) {
	var tempPosWin = 0;
	var tempPosEwWin = 0;
	var indexArray = [];
	var indexMaxArray = [];
	var tempOdd;
	var tempEwOdd;
	var tempIterator;
	var numOfSysOptions;
	var sysPerBetStake;
	var k = sysBetSelectedValue;
	var i;
	for (i = 0; i < k; i++) {
		indexArray[i] = i;
		indexMaxArray[i] = allBets.length - i;
	}
	indexMaxArray = indexMaxArray.reverse();
	tempIterator = k - 1;
	var m, j;
	while (indexArray[0] <= indexMaxArray[0]) {
		if (indexArray[tempIterator] < indexMaxArray[tempIterator]) {
			if (tempIterator !== k - 1) {
				tempIterator = k - 1;
				continue;
			}
			tempOdd = 1;
			tempEwOdd = 1;
			for (m = 0; m < k; m++) {
				if (allBets[indexArray[m]].incInSysCalc && !allBets[indexArray[m]].banker) {
					tempOdd *= allBets[indexArray[m]].price;
					tempEwOdd *=
						allBets[indexArray[m]].ewAllowed && allBets[indexArray[m]].ewCoeff
							? Math.round(
									((allBets[indexArray[m]].price - 1) /
										allBets[indexArray[m]].ewCoeff +
										1) *
										100
							  ) / 100
							: allBets[indexArray[m]].price;
				} else {
					tempOdd = 0;
					tempEwOdd = 0;
				}
			}
			tempPosWin = tempPosWin + mathCuttingFunction(tempOdd * 100) / 100;
			tempPosEwWin = tempPosEwWin + mathCuttingFunction(tempEwOdd * 100) / 100;
			// tempPosWin += tempOdd;
			// tempPosEwWin += tempEwOdd;
			indexArray[tempIterator]++;
		} else {
			tempIterator--;

			indexArray[tempIterator]++;

			for (j = tempIterator; j < k - 1; j++) {
				indexArray[j + 1] = indexArray[j] + 1;
			}
		}
	}
	numOfSysOptions = calculateSystemOptionsCount(k, allBets.length, bankerBetsCount);
	sysPerBetStake = stake / numOfSysOptions;
	if (eachWayMode) {
		sysPerBetStake /= 2;
	}

	return {
		win: (mathCuttingFunction(tempPosWin * sysPerBetStake * 100) / 100).toFixed(2),
		ewWin: (mathCuttingFunction(tempPosEwWin * sysPerBetStake * 100) / 100).toFixed(2),
		options: numOfSysOptions,
	};
}
/**
 * @name calcBetterOdd
 * @description calculates the better price
 * @param {Number} price
 * @returns Number
 */
export function calcBetterOdd(price) {
	price = parseFloat(price);
	if (price < 1.06) {
		return price;
	}
	price = (price - 1) * 0.1 + price;
	price = price > 10 ? Math.ceil(price) : +price.toFixed(2);
	return price;
}

export function calculatePossibleWin(
	allBets,
	unitStake,
	loggedIn,
	currency,
	betslip,
	bonusesRules,
	taxPercent,
	oddsFormat
) {
	let totalOdd = 1,
		ewOdd = 1,
		possibleWin = 0,
		bankerTotalPrice = 1,
		oddLimitExceededFlag = false,
		expBonus,
		posWin = 0;
	function setMaxWinLimit(value) {
		if (loggedIn && !isNaN(currency.rounding)) {
			if (
				Config.betting.maxWinLimit &&
				currency.rate &&
				value * currency.rate > Config.betting.maxWinLimit
			) {
				return parseFloat(
					(Config.betting.maxWinLimit / currency.rate).toFixed(
						Math.abs(currency.rounding)
					)
				);
			}
			return parseFloat(value.toFixed(Math.abs(currency.rounding)));
		}
		return parseFloat(value);
	}
	allBets.forEach(function (bet) {
		let singleStake = betslip.freeBet
			? Number.parseFloat(betslip.selectedFreeBetAmount) || 0
			: bet.singleStake;
		switch (betslip.type) {
			case BETSLIP_TYPE_SINGLE:
				if (allBets.length === 1 && !Config.betting.alternativeBetSlip) {
					bet.singleUnitStake = unitStake;
					bet.eachWay = betslip.eachWayMode;
				}
				if (
					!isNaN(parseFloat(singleStake)) &&
					parseFloat(singleStake) > 0 &&
					!bet.deleted &&
					(!betslip.horseEvents[bet.eventId] ||
						betslip.horseEvents[bet.eventId].type !== "SP") &&
					!bet.blocked
				) {
					var realPrice =
						Config.betting.enableCounterOffer && betslip.counterOffer
							? parseFloat(bet.counterOfferOdd) ||
							  bet.price ||
							  bet.initialPrice
							: betslip.betterOddSelectionMode
							? bet.betterPrice
							: bet.price;
					if (bet.eachWay && bet.ewAllowed && bet.ewCoeff) {
						bet.singlePosWin = setMaxWinLimit(
							Math.round(
								((realPrice - 1) / bet.ewCoeff + 1 + realPrice) *
									bet.singleUnitStake *
									100 || 0
							) / 100
						);
					} else {
						bet.singlePosWin = setMaxWinLimit(
							Math.round(realPrice * singleStake * 100 || 0) / 100
						);
					}
					betslip.freeBet &&
						betslip.selectedFreeBetAmount &&
						(bet.singlePosWin -= betslip.selectedFreeBetAmount);
					possibleWin += bet.singlePosWin;
				} else {
					bet.singlePosWin = 0;
				}
				break;
			case BETSLIP_TYPE_EXPRESS: //express
				totalOdd *= bet.price;
				ewOdd *=
					bet.ewAllowed && bet.ewCoeff
						? Math.round(((bet.price - 1) / bet.ewCoeff + 1) * 1000) / 1000
						: bet.price;
				break;
		}
	});

	switch (betslip.type) {
		case BETSLIP_TYPE_SINGLE:
			if (
				allBets.length === 1 &&
				!Config.betting.alternativeBetSlip &&
				betslip.stake &&
				(betslip.freeBet || betslip.bonusBet)
			) {
				possibleWin -= betslip.stake;
			}
			expOdds = allBets[0]?.price;
			posWin = setMaxWinLimit(Math.round(possibleWin * 100 || 0) / 100);
			break;
		case BETSLIP_TYPE_EXPRESS:
			if (Config.betting.totalOddsMax && totalOdd > Config.betting.totalOddsMax) {
				totalOdd = Config.betting.totalOddsMax;
				if (Config.betting.enableLimitExceededNotifications && !oddLimitExceededFlag) {
					oddLimitExceededFlag = true;
				}
			} else {
				oddLimitExceededFlag = false;
			}
			Config.main.mathCuttingFunctionForExpOdds &&
				(totalOdd = mathCuttingFunction(totalOdd * 100) / 100);
			var expOdds = OddConverter(totalOdd, oddsFormat);

			const stakeWithTax = betslip.stake * (1 - (taxPercent || 0) / 100);

			expBonus = calculateExpressBonus(
				allBets,
				stakeWithTax,
				bonusesRules,
				false,
				currency,
				oddsFormat
			);
			if (betslip.eachWayMode && ewOdd > 1 && unitStake) {
				posWin = setMaxWinLimit(Math.round((totalOdd + ewOdd) * unitStake * 100 || 0) / 100);
				break;
			}
			if (
				(betslip.stake && (betslip.freeBet || betslip.bonusBet)) ||
				(betslip.freeBet && betslip.selectedFreeBetAmount)
			) {
				let stake = (betslip.freeBet && betslip.selectedFreeBetAmount) || betslip.stake;
				posWin = setMaxWinLimit(Math.round((totalOdd * stake - stake) * 100 || 0) / 100);
				break;
			}
			posWin = setMaxWinLimit(
				Math.floor(
					expOdds *
						((betslip.freeBet && betslip.selectedFreeBetAmount) || betslip.stake) *
						100 || 0
				) / 100
			);
			break;
		case BETSLIP_TYPE_SYSTEM:
			if (allBets.length > 2) {
				let stake = (betslip.freeBet && betslip.selectedFreeBetAmount) || betslip.stake;
				var tempResult = calculateSystemPossibleWin(
					allBets,
					betslip.sysOptionValue,
					stake,
					betslip.bankersBetsCount,
					betslip.eachWayMode
				);
				if (betslip.eachWayMode) {
					posWin = setMaxWinLimit(
						Math.round((tempResult.win + tempResult.ewWin) * 1000 || 0) / 1000
					);
					break;
				}
				if (
					(betslip.stake && (betslip.freeBet || betslip.bonusBet)) ||
					(betslip.freeBet && betslip.selectedFreeBetAmount)
				) {
					posWin = setMaxWinLimit(
						Math.round((bankerTotalPrice * tempResult.win - stake) * 1000 || 0) /
							1000
					);
					break;
				}
				posWin = setMaxWinLimit(
					Math.round(bankerTotalPrice * tempResult.win * 1000 || 0) / 1000
				);
				break;
			}
			break;
		default:
			break;
	}
	return { posWin, expOdds, expBonus, oddLimitExceededFlag };
}

export function roundStake(stake, rounding) {
	const factor = Math.pow(10, rounding);
	return Math.trunc(mathCuttingFunction((parseFloat(stake) || 0) * factor)) / factor;
}

export function calculatePossibleWinForUk(allBets, loggedIn, currency, betslip, multipleOptions) {
	let bankerTotalPrice = 1,
		posWin = 0,
		multipleOptionsCopy = Helpers.cloneDeep(multipleOptions);

	delete multipleOptionsCopy.enabled;
	delete multipleOptionsCopy.Singles;

	function setMaxWinLimit(value) {
		if (loggedIn && !isNaN(currency.rounding)) {
			if (
				Config.betting.maxWinLimit &&
				currency.rate &&
				value * currency.rate > Config.betting.maxWinLimit
			) {
				return parseFloat(
					(Config.betting.maxWinLimit / currency.rate).toFixed(
						Math.abs(currency.rounding)
					)
				);
			}
			return parseFloat(value.toFixed(Math.abs(currency.rounding)));
		}
		return parseFloat(value);
	}

	multipleOptions &&
		Object.keys(multipleOptionsCopy).map(key => {
			if (multipleOptionsCopy[key].opt <= allBets.length) {
				let stake =
						1 *
						((betslip.freeBet && betslip.selectedFreeBetAmount) ||
							1 *
								multipleOptionsCopy[key].value *
								multipleOptionsCopy[key].quantity),
					tempResult = calculateSystemPossibleWin(
						allBets,
						multipleOptionsCopy[key].opt,
						stake
					);
				posWin += setMaxWinLimit(
					Math.round(bankerTotalPrice * tempResult.win * 1000 || 0) / 1000
				);
			} else {
				let stake =
					1 *
					((betslip.freeBet && betslip.selectedFreeBetAmount) ||
						1 * multipleOptionsCopy[key].value);
				posWin += Object.keys(multipleOptionsCopy).reduce((calculated, key) => {
					if (multipleOptionsCopy[key].opt <= allBets.length) {
						let tempResult = calculateSystemPossibleWin(
							allBets,
							multipleOptionsCopy[key].opt,
							stake * multipleOptionsCopy[key].quantity
						);
						calculated += setMaxWinLimit(
							Math.round(bankerTotalPrice * tempResult.win * 1000 || 0) / 1000
						);
					}
					return calculated;
				}, 0);
			}
		});

	betslip.freeBet && betslip.selectedFreeBetAmount && posWin && (posWin -= betslip.selectedFreeBetAmount);
	return { posWin };
}

export function calculateExpressBonus(allBets, stake, bonusesRules = [], ignoreConfig = false, currency, oddsFormat) {
	if (!Config.betting.enableExpressBonus && !ignoreConfig) {
		return;
	}
	const amountArray = bonusesRules.map(item =>
		expressBonusCalculator(item, allBets, stake, currency, oddsFormat)
	);
	amountArray.sort((a, b) => b - a);
	return Math.round(amountArray[0] * 100 || 0) / 100;
}

/**
 * @param {Object} rule bonus rule,
 * @param {Array} bets array of events,
 * @param {Number} stake stake of events,
 * @description calculate express bonus
 * @returns {Number} amount for this rule if not math return 0
 */
function expressBonusCalculator(rule, bets = [], stake, currency, oddsFormat) {
	// if (rule.IgnoreLowOddSelection) {
	//     const filtrbets = bets.filter(item => item.price > rule.MinOdds);
	//     filtrbets.sort((a, b) => b.price - a.price);
	//     const sortedArray = filtrbets.slice(0, rule.MaximumSelections);
	//     if (
	//         sortedArray.reduce((mul, item) => mul * item.price, 1) > rule.MinBetOdds &&
	//         sortedArray.length <= rule.MaximumSelections && sortedArray.length >= rule.MinimumSelections
	//     ) {
	//         const profit = (stake * (sortedArray.reduce((mul, item) => mul * item.price, 1) - 1)); // to do
	//         if (rule.AmountPercent < 0) {
	//             return (profit * sortedArray.length) / 100; // to do
	//         } else {
	//             return (profit * rule.AmountPercent) / 100;
	//         }
	//     } else {
	//         return 0;
	//     }
	// } else {
	//     if (
	//         bets.every(item => item.price >= rule.MinOdds) &&
	//         bets.reduce((mul, item) => mul * item.price, 1) > rule.MinBetOdds &&
	//         bets.length <= rule.MaximumSelections && bets.length >= rule.MinimumSelections
	//     ) {
	//         const profit = (stake * (bets.reduce((mul, item) => mul * item.price, 1) - 1));
	//         if (rule.AmountPercent < 0) {
	//             return (profit * bets.length) / 100;
	//         } else {
	//             return (profit * rule.AmountPercent) / 100;
	//         }
	//     } else {
	//         return 0;
	//     }
	// }
	let filtrbets = bets;
	if (rule.IgnoreLowOddSelection) {
		filtrbets = bets.filter(item => item.price > rule.MinOdds);
	}
	if (
		filtrbets.every(item => item.price >= rule.MinOdds) &&
		filtrbets.reduce((mul, item) => mul * item.price, 1) > rule.MinBetOdds &&
		filtrbets.length >= rule.MinimumSelections &&
		(rule.MaximumSelections === null ? true : filtrbets.length <= rule.MaximumSelections)
	) {
		const allOdds = bets.reduce((mul, item) => mul * item.price, 1);
		let profit;
		const stakeRounded = parseFloat(stake).toFixed(Math.abs(currency.rounding));
		const allOddsConverted = OddConverter(allOdds, oddsFormat);

		// console.log(OddConverter(allOdds, oddsFormat), 'awqdwq', {allOdds, oddsFormat})

		switch (rule.Basis) {
			case 0:
				profit = stakeRounded;
				break;
			case 1:
				profit =
					stakeRounded *
					((Config.betting.totalOddsMax < allOddsConverted
						? Config.betting.totalOddsMax
						: allOddsConverted) -
						1);
				break;
			case 2:
				profit =
					stakeRounded *
					(Config.betting.totalOddsMax < allOddsConverted
						? Config.betting.totalOddsMax
						: allOddsConverted);
		}
		// TO DO
		if (rule.AmountPercent < 0) {
			return ((profit || 1) * filtrbets.length) / 100; // to do
		} else {
			return ((profit || 1) * rule.AmountPercent) / 100;
		}
	} else {
		return 0;
	}
}

/**
 * Creates the do_bet requests based on betslip state
 * @param {Object} betSlipData betslip state
 * @param {Object} currency  currency object
 * @param {bool} forFreeBet is request is being created for getting free bet data
 * @returns {Array}
 */
export const createBetRequests = (betSlipData, currency, forFreeBet = false) => {
	var requests = [],
		data;
	var currentBets;
	let hasLiveEvents = Object.keys(betSlipData.events).reduce((acc, id) => {
		return acc || betSlipData.events[id].isLive;
	}, false);

	let autoSuperBet =
		hasLiveEvents &&
		Config.betting.autoSuperBetLimit &&
		currency &&
		currency.name &&
		Config.betting.autoSuperBetLimit[currency.name] &&
		betSlipData.stake >= Config.betting.autoSuperBetLimit[currency.name];

	let setCommonFields = data => {
		data.type =
			betSlipData.type === 40 || betSlipData.type === 43
				? betSlipData.localBetslipHorseTypeOnlyTryCastForCast
				: betSlipData.type;
		data.source = Config.betting.bet_source;
		// data.mode = (betSlipData.superBet || autoSuperBet) ? BetPriceChangeEnum.AcceptAnyOdds : parseInt(betSlipData.acceptPriceChanges, 10); !WRONG Logic about super bet

		data.mode = parseInt(betSlipData.acceptPriceChanges, 10);

		data.each_way = betSlipData.eachWayMode;
		if (!forFreeBet) {
			data.amount = parseFloat(betSlipData.stake);
		} else {
			data.is_live = hasLiveEvents;
		}
		if (betSlipData.freeBet && betSlipData.selectedFreeBetId) {
			data.client_bonus_id = betSlipData.selectedFreeBetId;
			data.amount = betSlipData.selectedFreeBetAmount;
		}
		if (betSlipData.odd_type || betSlipData.odd_type === 0) {
			data.odd_type = betSlipData.odd_type;
		}
		return data;
	};
	switch (betSlipData.type) {
		case BETSLIP_TYPE_SINGLE:
			Object.keys(betSlipData.events).map(id => {
				//console.error(betSlipData.horseEvents[id] && betSlipData.horseEvents[id].type === "SP");
				let bet = betSlipData.events[id];

				let price = betSlipData.betterOddSelectionMode
					? calcBetterOdd(bet.price || bet.initialPrice)
					: bet.price || bet.initialPrice;

				if (
					bet.oddType === "HORSE" &&
					betSlipData.horseEvents[id] &&
					betSlipData.horseEvents[id].type === "SP"
				)
					price = -1;

				if (bet.offeredPrice) price = +bet.offeredPrice;

				data = {
					// 'is_offer': betSlipData.betterOddSelectionMode ? 1 : 0,
					is_offer: +isCounterOffer(betSlipData),
					bets: [
						{
							event_id: bet.eventId,
							price: price,
						},
					],
				};
				setCommonFields(data);
				!forFreeBet && (data.amount = parseFloat(bet.singleStake));
				requests.push(data);
			});
			break;
		case 40:
		case 43: {
			if (
				betSlipData.localBetslipHorseTypeOnlyTryCastForCast === 40 ||
				betSlipData.localBetslipHorseTypeOnlyTryCastForCast === 43
			) {
				currentBets = [];
				Object.keys(betSlipData.events).map(id => {
					let bet = betSlipData.events[id];
					currentBets.push({
						event_id: bet.eventId,
						price: -1,
						order: +bet.forecastOrTricastTypeOrderArray[0],
					});
				});
				data = { bets: currentBets };
				setCommonFields(data);
				requests.push(data);
				break;
			}
			currentBets = [];
			Object.keys(betSlipData.events).map(id => {
				let bet = betSlipData.events[id];
				currentBets.push({ event_id: bet.eventId, price: -1 });
			});
			data = { bets: currentBets };
			setCommonFields(data);
			requests.push(data);
			break;
		}
		case BETSLIP_TYPE_EXPRESS:
			currentBets = [];
			Object.keys(betSlipData.events).map(id => {
				let bet = betSlipData.events[id];
				currentBets.push({
					event_id: bet.eventId,
					price:
						betSlipData.horseEvents[id] && betSlipData.horseEvents[id].type === "SP"
							? -1
							: bet.price || bet.initialPrice,
				});
			});
			data = { bets: currentBets };
			setCommonFields(data);
			requests.push(data);
			break;
		case BETSLIP_TYPE_SYSTEM:
			currentBets = [];
			Object.keys(betSlipData.events).map(id => {
				let bet = betSlipData.events[id];
				currentBets.push({
					event_id: bet.eventId,
					price:
						betSlipData.horseEvents[id] && betSlipData.horseEvents[id].type === "SP"
							? -1
							: bet.price,
					banker: bet.banker ? 1 : 0,
				});
			});
			data = {
				bets: currentBets,
				sys_bet:
					parseInt(betSlipData.sysOptionValue, 10) +
					(betSlipData.bankerBetsCount ? betSlipData.bankerBetsCount : 0),
			};
			setCommonFields(data);
			requests.push(data);
			break;
		case BETSLIP_TYPE_CHAIN:
			currentBets = [];
			Object.keys(betSlipData.events).map(id => {
				let bet = betSlipData.events[id];
				// eslint-disable-next-line camelcase
				currentBets.push({
					event_id: bet.eventId,
					price:
						betSlipData.horseEvents[id] && betSlipData.horseEvents[id].type === "SP"
							? -1
							: bet.price || bet.initialPrice,
				});
			});
			data = { bets: currentBets };
			setCommonFields(data);
			requests.push(data);
			break;

		default:
			break;
	}
	return requests;
};

export const makeQuickBet = (payload, bet) => {
	return {
		type: MAKE_QUICK_BET,
		meta: {
			bet,
		},
		payload,
	};
};

export const toggleEvent = (game, market, event, sportType, payload, isBoosted) => {
	return {
		type: BETSLIP_TOGGLE_EVENT,
		payload,
		meta: {
			game,
			market,
			event,
			sportType,
			isBoosted,
		},
	};
};

export const addEventCompareConflicts = (game, market, event, order, payload) => {
	return {
		type: BETSLIP_ADD_COMPARE_EVENT,
		payload,
		meta: {
			game,
			market,
			event,
			order,
		},
	};
};

export const toggleHorseEvent = (type, gameId, id) => {
	return {
		type: BETSLIP_TOGGLE_HORSE_EVENT,
		payload: { type, id, gameId },
	};
};

/**
 * Creates the do_bet requests based on betslip state
 * @param {Object} betSlipData betslip state
 * @param {Object} currency  currency object
 * @param {bool} forFreeBet is request is being created for getting free bet data
 * @returns {Array}
 */
export const createBetRequestsForUkBetslip = (betSlipData, currency, forFreeBet = false) => {
	let requests = [],
		data,
		bets = [],
		hasLiveEvents = Object.keys(betSlipData.events).reduce((acc, id) => {
			return acc || betSlipData.events[id].isLive;
		}, false),
		autoSuperBet =
			hasLiveEvents &&
			Config.betting.autoSuperBetLimit &&
			currency &&
			currency.name &&
			Config.betting.autoSuperBetLimit[currency.name] &&
			betSlipData.stake >= Config.betting.autoSuperBetLimit[currency.name];

	let setCommonFields = (data, type, sysBet) => {
		data.type = type || betSlipData.type;
		data.source = Config.betting.bet_source;
		data.mode = betSlipData.superBet || autoSuperBet ? -1 : parseInt(betSlipData.acceptPriceChanges, 10);
		data.each_way = betSlipData.eachWayMode;
		if (forFreeBet) {
			data.is_live = hasLiveEvents;
		}
		if (betSlipData.freeBet && betSlipData.selectedFreeBetId && !Object.keys(additionalBets).length) {
			data.client_bonus_id = betSlipData.selectedFreeBetId;
			data.amount = betSlipData.selectedFreeBetAmount;
		}
		//if DOUBLES add sys_option, banker to request
		if (data.type === 3) {
			data.sys_bet = sysBet;
		}
		return data;
	};

	let additionalBets = Helpers.cloneDeep(betSlipData.multipleOptions || {});
	delete additionalBets.enabled;

	Object.keys(betSlipData.events).map(id => {
		let bet = betSlipData.events[id];
		bets.push({
			event_id: bet.eventId,
			price:
				bet.oddType === "SP"
					? -1
					: betSlipData.betterOddSelectionMode
					? calcBetterOdd(bet.price || bet.initialPrice)
					: bet.price || bet.initialPrice,
		});
		if (
			(forFreeBet && !Object.keys(additionalBets).length) ||
			(!forFreeBet && !!parseFloat(bet.singleStake))
		) {
			data = {
				is_offer: betSlipData.betterOddSelectionMode ? 1 : 0,
				bets: [
					{
						event_id: bet.eventId,
						price:
							bet.oddType === "SP"
								? -1
								: betSlipData.betterOddSelectionMode
								? calcBetterOdd(bet.price || bet.initialPrice)
								: bet.price || bet.initialPrice,
					},
				],
			};
			setCommonFields(data);
			!forFreeBet && (data.amount = parseFloat(bet.singleStake));
			requests.push(data);
		}
	});

	delete additionalBets.Singles;

	Object.keys(additionalBets).map(key => {
		let value = forFreeBet ? betSlipData.selectedFreeBetAmount : parseFloat(additionalBets[key].value);
		if ((!forFreeBet && value) || (forFreeBet && additionalBets[key].quantity === 1)) {
			data = {
				is_offer: betSlipData.betterOddSelectionMode ? 1 : 0,
				bets,
			};
			setCommonFields(data, additionalBets[key].type, additionalBets[key].opt);

			data.amount = additionalBets[key].quantity * value;
			requests.push(data);
		}
	});

	return requests;
};

export const isCounterOffer = betSlip =>
	Config.betting.enableCounterOffer && betSlip.acceptPriceChanges === BetPriceChangeEnum.CounterOffer;

/**
 * Does some calculations for betslip template
 * @param {Object} betSlip betslip data from store
 * @param {Object} data betslip events data from swarm
 * @param {Object} user user data from store
 * @param {Object} currency currency data from store
 * @param {Function} dispatch store's dispatch function
 * @param {Number|null|undefined} terminalData store's dispatch function
 * @returns {{events: Array, info: {}, posWin: *, expOdds: number, expBonus: *, freeBetAvailable: (*|Array|Number), priceChangeNeeds2bConfirmed: (*|boolean), betsCannotBePlaced: (boolean|*), displayInfo: {warning: Array, error: Array, info: Array}, systemOptions: *}}
 */
export function doBetSlipViewCalculations(
	betSlip,
	data,
	user,
	currency,
	dispatch,
	terminalData,
	terminalBalance,
	isSuperBet,
	oddsFormat,
	partnerConfig,
	taxPercent
) {
	let loggedIn = user.loggedIn,
		{ events, info, totalStake } = getBetslipData(data, betSlip, null, terminalData, partnerConfig),
		{ posWin, expOdds, expBonus, oddLimitExceededFlag } = calculatePossibleWin(
			events,
			0,
			loggedIn,
			currency,
			betSlip,
			terminalData.bonuses,
			taxPercent,
			oddsFormat
		); // modifies events!
	if (betSlip.type !== BETSLIP_TYPE_SINGLE || betSlip.quickBet) {
		totalStake = parseFloat(betSlip.stake);
	}
	let systemOptions;
	if (betSlip.type === BETSLIP_TYPE_SYSTEM) {
		systemOptions = SystemOptionsSelect(betSlip, dispatch);
	}

	const authData = JSON.parse(sessionStorage.getItem("authData")),
		currencyType = authData.currency;
	// TO DO --START
	const balance =
		sessionStorage.getItem("balance") && JSON.parse(sessionStorage.getItem("balance")).session_balance;
	const userBalance = user.profile && user.profile.balance;
	let currentbalance;
	if (userBalance) {
		currentbalance = userBalance;
	} else {
		if (terminalBalance !== undefined) {
			if (terminalBalance !== null) {
				currentbalance = terminalBalance;
			} else {
				if (balance === null) {
					currentbalance = 0;
				} else {
					currentbalance = balance;
				}
			}
		} else {
			if (balance === null) {
				currentbalance = 0;
			} else {
				currentbalance = balance;
			}
		}
	}
	let price = expOdds || (events.length > 0 && events[0] ? events[0].price : 1);
	let maxAllowedStakeFromConfig =
		Config.betting.maxPossibleWin &&
		posWin > Config.betting.maxPossibleWin &&
		Math.floor(Config.betting.maxPossibleWin / price);
	maxAllowedStakeFromConfig =
		betSlip.type !== BETSLIP_TYPE_SYSTEM
			? maxAllowedStakeFromConfig
			: posWin > Config.betting.maxPossibleWin &&
			  Math.floor(Config.betting.maxPossibleWin / (posWin / betSlip.stake)); //for system bet use different logic of calculating max stake
	let maxAllowedStake =
		maxAllowedStakeFromConfig || maxAllowedStakeFromConfig === 0
			? Math.min(maxAllowedStakeFromConfig, terminalData.startupSettings.MaxBet)
			: terminalData.startupSettings.MaxBet;
	let minAllowedStake = terminalData.startupSettings.MinBet;
	// TO DO --END
	const horseBetSlipLocalTypes = (function () {
		// forecast
		let horseEventHasConflict = false;
		let localBetslipHorseType = "NO_FORECAST";
		if (betSlip.type < 40 || events.length < 1) {
			return { horseEventHasConflict, localBetslipHorseType };
		}
		// here I mutate the data forecastOrTricastType and forecastOrTricastTypeOrderArray not exist in redux
		// it very bad, be careful
		events.forEach(event => {
			const [forecastOrTricastType, combinationType] = event.horseType?.split("$"); //forecast$2  forecast$1_2
			event.forecastOrTricastType = forecastOrTricastType;
			event.forecastOrTricastTypeOrderArray = combinationType?.split("_") || [];
		});
		const [event] = events;
		if (betSlip.type === 40) {
			if (event.forecastOrTricastTypeOrderArray.length === 1) {
				localBetslipHorseType = 40;
				horseEventHasConflict = events.length !== 2;
			} else if (event.forecastOrTricastTypeOrderArray.length === 2) {
				localBetslipHorseType = events.length === 2 ? 41 : 42;
				horseEventHasConflict = events.length < 2;
			} else {
				console.warn("Something is incorrect");
			}
		}
		if (betSlip.type === 43) {
			if (event.forecastOrTricastTypeOrderArray.length === 1) {
				localBetslipHorseType = 43;
				horseEventHasConflict = events.length !== 3;
			} else if (event.forecastOrTricastTypeOrderArray.length === 3) {
				localBetslipHorseType = 44;
				horseEventHasConflict = events.length < 3;
			} else {
				console.warn("Something is incorrect");
			}
		}
		return { horseEventHasConflict, localBetslipHorseType };
	})();
	const eventsArray = Object.keys(betSlip.events).filter(item =>
		Object.prototype.hasOwnProperty.call(betSlip.events, item)
	);

	const { MinimalOdd } = terminalData.startupSettings;
	const { minimum_offer_amount } = partnerConfig || {};
	const minimalOddValid = betSlip.type === BETSLIP_TYPE_SYSTEM || !MinimalOdd || expOdds >= MinimalOdd;
	const minimumAmountValid =
		!events.length ||
		!isCounterOffer(betSlip) ||
		!minimum_offer_amount ||
		betSlip.unitStake >= minimum_offer_amount;
	const offeredPriceValid =
		!isCounterOffer(betSlip) ||
		events.every(event => _.inRange(event.offeredPrice, event.price, event.maxOddToOffer + 0.01));

	let totalBonus = user.profile
			? (user.profile.bonus_balance || 0) +
			  (user.profile.bonus_win_balance || 0) +
			  (user.profile.frozen_balance || 0)
			: 0,
		eventsCount = Object.keys(betSlip.events).length,
		freeBetAvailable =
			user.profile &&
			user.profile.has_free_bets &&
			betSlip.freeBetsList &&
			betSlip.freeBetsList.length &&
			!betSlip.freeBetLoading &&
			!!eventsCount &&
			((betSlip.type === BETSLIP_TYPE_SINGLE && eventsCount === 1) ||
				betSlip.type !== BETSLIP_TYPE_SINGLE) &&
			!betSlip.eachWayMode,
		insufficientBalance = !(freeBetAvailable && betSlip.freeBet && betSlip.selectedFreeBetId)
			? user.profile && totalStake > user.profile.balance && totalStake > totalBonus
			: false,
		priceChangeNeeds2bConfirmed =
			(info.priceWentUp && betSlip.acceptPriceChanges !== BetPriceChangeEnum.AcceptAnyOdds) || // price went up and user is not accepting "any" changes
			(info.priceChange && betSlip.acceptPriceChanges === BetPriceChangeEnum.AlwaysAsk),
		betsCannotBeBooked =
			horseBetSlipLocalTypes.horseEventHasConflict ||
			(info.hasConflicts && betSlip.type < 40) ||
			info.hasLockedEvents ||
			(betSlip.type !== BETSLIP_TYPE_SINGLE && (events.length < 2 || info.hasSingleOnlyEvents)),
		allEventsHasStake = eventsArray.every(item => parseFloat(betSlip.events[item].singleStake)),
		betsCannotBePlaced =
			(events.some(item => item.isBoosted) &&
				events.length < Config.betting.boostedMinCountInBetslip) ||
			(betSlip.type !== BETSLIP_TYPE_SINGLE && (events.length < 2 || info.hasSingleOnlyEvents)) ||
			(betSlip.type === BETSLIP_TYPE_SYSTEM && events.length < 3) ||
			(betSlip.type !== BETSLIP_TYPE_SINGLE &&
				!betSlip.stake &&
				!(freeBetAvailable && betSlip.freeBet && betSlip.selectedFreeBetId)) ||
			priceChangeNeeds2bConfirmed ||
			info.hasDeletedEvents ||
			!minimalOddValid ||
			!minimumAmountValid ||
			!offeredPriceValid ||
			horseBetSlipLocalTypes.horseEventHasConflict ||
			(info.hasConflicts && betSlip.type < 40) || //tricast forecast check
			info.hasEmptyStakes ||
			info.hasLockedEvents ||
			info.hasWrongStakes ||
			info.baseChange ||
			oddLimitExceededFlag ||
			(betSlip.type === BETSLIP_TYPE_SINGLE &&
				!betSlip.freeBet &&
				eventsArray.reduce(
					(sum, item) => sum + parseFloat(betSlip.events[item].singleStake) || 0,
					0
				) > parseFloat(currentbalance)) || // TO DO
			(betSlip.type === BETSLIP_TYPE_SINGLE &&
				!betSlip.freeBet &&
				eventsArray.some(
					item =>
						parseFloat(betSlip.events[item].singleStake) < minAllowedStake ||
						(!isSuperBet &&
							parseFloat(betSlip.events[item].singleStake) > maxAllowedStake)
				)) ||
			(betSlip.type === BETSLIP_TYPE_EXPRESS &&
				!betSlip.freeBet &&
				(minAllowedStake > betSlip.stake ||
					((!isSuperBet || Config.betting.maxPossibleWin) &&
						betSlip.stake > maxAllowedStake))) ||
			(betSlip.type === BETSLIP_TYPE_SYSTEM &&
				!betSlip.freeBet &&
				Config.betting.maxPossibleWin &&
				betSlip.stake > maxAllowedStake) || //for system only check if MaxPossibleWin limit exists
			(Config.betting.enableBankerBet && events.length - betSlip.bankerBetsCount < 2) ||
			(betSlip.type === BETSLIP_TYPE_SINGLE &&
				info.hasEventsFromSameGame &&
				Config.betting.blockSingleGameBets) ||
			(!loggedIn && !Config.betting.allowBetWithoutLogin) ||
			insufficientBalance ||
			!events.length ||
			(info.hasSingleOnlyEvents && events.length > 1 && betSlip.type !== BETSLIP_TYPE_SINGLE), // betslip contains events that cannot be combined with others
		displayInfo = { warning: [], error: [], info: [] };

	const minimumOfferAmount = Config.useCurrencySymbol
		? Config.main.currency[currencyType] + minimum_offer_amount
		: `${minimum_offer_amount} ${currencyType}`;

	!minimalOddValid &&
		displayInfo.warning.push(
			t(
				"Minimum odd should be {1} . Change events to place a bet.",
				OddConverter(MinimalOdd, oddsFormat)
			)
		);
	!minimumAmountValid &&
		displayInfo.warning.push(t(`Minimum stake for offer should be {1}.`, minimumOfferAmount));

	isCounterOffer(betSlip) &&
		events.forEach(event => {
			const inRange = _.inRange(event.offeredPrice, event.price, event.maxOddToOffer + 0.01);

			!inRange &&
				displayInfo.warning.push(
					t(
						"Offer price should be between {1} - {2}.",
						OddConverter(event.price, oddsFormat),
						OddConverter(event.maxOddToOffer, oddsFormat)
					)
				);
		});

	!events.length && displayInfo.info.push(t("Betslip is empty. To select a bet, please click on any odd."));
	info.hasDeletedEvents && displayInfo.warning.push(t("Some events in betslip are not available at the moment."));
	priceChangeNeeds2bConfirmed && displayInfo.warning.push(t("Some events in betslip have changed price."));
	info.baseChange && displayInfo.warning.push(t("Some events in betslip have changed base."));
	events.some(item => item.isBoosted) &&
		events.length < Config.betting.boostedMinCountInBetslip &&
		displayInfo.error.push(
			t(
				`To include Boosted Odd you need to have at least ${Config.betting.boostedMinCountInBetslip} selections`
			)
		);

	betSlip.type === BETSLIP_TYPE_EXPRESS &&
		!betSlip.freeBet &&
		(maxAllowedStakeFromConfig || maxAllowedStakeFromConfig === 0) &&
		displayInfo.warning.push(t("Max possible win amount exceeds"));
	betSlip.type === BETSLIP_TYPE_SINGLE &&
		!betSlip.freeBet &&
		(maxAllowedStakeFromConfig || maxAllowedStakeFromConfig === 0) &&
		displayInfo.warning.push(t("Max possible win amount exceeds"));
	betSlip.type === BETSLIP_TYPE_SYSTEM &&
		!betSlip.freeBet &&
		(maxAllowedStakeFromConfig || maxAllowedStakeFromConfig === 0) &&
		displayInfo.warning.push(t("Max possible win amount exceeds"));

	betSlip.type === BETSLIP_TYPE_SYSTEM &&
		!betSlip.freeBet &&
		Config.betting.maxPossibleWin &&
		(minAllowedStake > betSlip.stake || maxAllowedStake < betSlip.stake) &&
		displayInfo.warning.push(
			t(
				"Min stake is {1}, Max is {2}",
				Config.main.currency[terminalData.authData.currency] + minAllowedStake,
				Config.main.currency[terminalData.authData.currency] + maxAllowedStake
			)
		);
	betSlip.type === BETSLIP_TYPE_EXPRESS &&
		!betSlip.freeBet &&
		(!Config.betting.enableSuperBetAutomatically || Config.betting.maxPossibleWin) &&
		(minAllowedStake > betSlip.stake || maxAllowedStake < betSlip.stake) &&
		displayInfo.warning.push(
			t(
				"Min stake is {1}, Max is {2}",
				Config.main.currency[terminalData.authData.currency] + minAllowedStake,
				Config.main.currency[terminalData.authData.currency] + maxAllowedStake
			)
		);
	betSlip.type === BETSLIP_TYPE_SINGLE &&
		!betSlip.freeBet &&
		(!Config.betting.enableSuperBetAutomatically || Config.betting.maxPossibleWin) &&
		eventsArray.some(
			item =>
				parseFloat(betSlip.events[item].singleStake) < minAllowedStake ||
				parseFloat(betSlip.events[item].singleStake) > maxAllowedStake
		) &&
		displayInfo.warning.push(
			t(
				"Min stake is {1}, Max is {2}",
				Config.main.currency[terminalData.authData.currency] + minAllowedStake,
				Config.main.currency[terminalData.authData.currency] + maxAllowedStake
			)
		);
	betSlip.type === BETSLIP_TYPE_SINGLE &&
		eventsArray.reduce((sum, item) => sum + parseFloat(betSlip.events[item].singleStake) || 0, 0) >
			parseFloat(currentbalance) &&
		displayInfo.warning.push(t("Insufficient balance"));
	horseBetSlipLocalTypes.horseEventHasConflict && displayInfo.warning.push(t("Add new events"));
	info.hasConflicts &&
		betSlip.type < 40 &&
		displayInfo.warning.push(t("Some events in betslip conflict with others."));
	(((info.hasEmptyStakes && betSlip.freeBet) ||
		(info.hasWrongStakes && betSlip.freeBet) ||
		(!betSlip.stake && betSlip.type !== BETSLIP_TYPE_SINGLE)) &&
		!(freeBetAvailable && betSlip.freeBet && betSlip.selectedFreeBetId)) ||
		(betSlip.type === BETSLIP_TYPE_SINGLE &&
			!allEventsHasStake &&
			displayInfo.info.push(t("Set stake to place a bet.")));
	insufficientBalance &&
		!(
			Config.main.betslipAdditionalMessages &&
			Config.main.betslipAdditionalMessages["insufficientBalance"]
		) &&
		displayInfo.warning.push(t("Insufficient balance"));
	oddLimitExceededFlag && displayInfo.warning.push(t("Odds limit exceeded."));
	(!loggedIn && Config.main.setNotLoggedInCustomBetslipText && displayInfo.warning.push({ needToLogin: true })) ||
		// (!loggedIn && displayInfo.warning.push(t("You need to login to be able to place a bet.")));
		(((betSlip.type === BETSLIP_TYPE_SYSTEM && events.length && events.length < 3) ||
			(betSlip.type === BETSLIP_TYPE_EXPRESS && events.length && events.length < 2) ||
			(betSlip.type === BETSLIP_TYPE_CHAIN && events.length && events.length < 2)) &&
			displayInfo.info.push(t("Add more events to place a bet.")));
	return {
		events,
		info,
		posWin,
		expOdds,
		expBonus,
		freeBetAvailable,
		totalStake,
		priceChangeNeeds2bConfirmed,
		betsCannotBePlaced,
		betsCannotBeBooked,
		displayInfo,
		systemOptions,
		insufficientBalance,
		horseBetSlipLocalTypes,
	};
}

/**
 * Does some calculations for betslip template
 * @param {Object} betSlip betslip data from store
 * @param {Object} data betslip events data from swarm
 * @param {Object} user user data from store
 * @param {Object} currency currency data from store
 * @param {object} multipleOptions descriptor
 * @param {number} minBetAmount
 * @returns {{events: Array, info: {}, posWin: *, expOdds: number, expBonus: *, freeBetAvailable: (*|Array|Number), priceChangeNeeds2bConfirmed: (*|boolean), betsCannotBePlaced: (boolean|*), displayInfo: {warning: Array, error: Array, info: Array}, systemOptions: *}}
 */
export function doBetSlipViewCalculationsForUk(betSlip, data, user, currency, multipleOptions, minBetAmount) {
	return doUkCalculations(JSON.stringify({ betSlip, data, user, currency, multipleOptions, minBetAmount }));
}

/**
 * Calculates stake/unit stake division coefficient
 * @param {Object} betslip store betslip data
 * @returns {number}
 */
export function getDivisionCoefficient(betslip) {
	let divisionCoefficient = 1;
	let betslipEventsCount = Object.keys(betslip.events).length;
	if (betslipEventsCount > 0) {
		var systemOptionsCount = calculateSystemOptionsCount(
			betslip.sysOptionValue,
			betslipEventsCount,
			betslip.bankersBetsCount
		);
		divisionCoefficient =
			(betslip.eachWayMode ? 2 : 1) *
			(betslip.type === BETSLIP_TYPE_SYSTEM && systemOptionsCount > 0 ? systemOptionsCount : 1);
	}
	return divisionCoefficient;
}

/**
 * Super Bet Processing
 * @param {Array} superBets Active Super bets
 * @param {Array} processedSuperBetIds super bet ids which already processed
 * @param {Function} dispatch
 * @returns {number}
 */
export function superBetWatcher(superBets, processedSuperBetIds, dispatch) {
	superBets.forEach(superBetInfo => {
		if (
			superBetInfo.super_bet_status !== undefined &&
			superBetInfo.super_bet_id &&
			!processedSuperBetIds[superBetInfo.super_bet_id]
		) {
			console.log("superbet response received!");
			let id = superBetInfo.super_bet_id;
			if (superBetInfo.super_bet_status === 1) {
				// dispatch(OpenPopup("message", { title: t("Success"), type: "info", body: t("Your Bet request {1} is accepted.", id) }));
				dispatch(OpenPopup("superbet", { disable: true, superBetIsAlreadyAccepted: true }));
				processedSuperBetIds[id] = true;
			} else if (superBetInfo.super_bet_status === -1) {
				// dispatch(OpenPopup("message", { title: t("Warning"), type: "warning", body: t("Your Bet request {1} is declined.", id) }));
				dispatch(OpenPopup("superbet", { disable: true, superBetIsAlreadyAccepted: false }));
				processedSuperBetIds[id] = true;
			} else if (superBetInfo.super_bet_status === 0) {
				dispatch(GetSuperBetInfo(id));
				processedSuperBetIds[id] = true;
			}
		}
	});
}

export const ukMultipleOptions = Object.freeze({
	3: [
		{
			type: 5,
			value: "Trixie",
		},
	],
	4: [
		{
			type: 6,
			value: "Yankee",
		},
	],
	5: [
		{
			type: 8,
			value: "Super Yankee",
		},
	],
	6: [
		{
			type: 9,
			value: "Heinz",
		},
	],
	7: [
		{
			type: 10,
			value: "Super Heinz",
		},
	],
	8: [
		{
			type: 11,
			value: "Goliath",
		},
	],
	simple: [
		{
			type: 1,
			size: 1,
			value: "Singles",
		},
		{
			type: 2,
			size: 2,
			value: "Doubles",
		},
	],
	default: [
		{
			type: 1,
			size: 1,
			value: "Singles",
		},
		{
			type: 2,
			size: 2,
			value: "Doubles",
		},
		{
			type: 2, //3
			size: 3,
			value: "Trebles",
		},
	],
});

export const getFolds = count => {
	let temp = [];
	for (let i = 4; i <= count; i++) {
		temp.push({
			type: 2,
			quantity: calculateSystemOptionsCount(i, count),
			value: i + " Folds",
			displayValue: `${i} ${t("Folds")}`,
		});
	}
	return temp;
};

const doUkCalculations = createSelector([selectorData => selectorData], selectorData => {
	let { betSlip, data, user, currency, multipleOptions, minBetAmount } = JSON.parse(selectorData),
		loggedIn = user.loggedIn,
		eventsCount = Object.keys(betSlip.events).length,
		freeBetAvailable =
			user.profile &&
			user.profile.has_free_bets &&
			betSlip.freeBetsList &&
			betSlip.freeBetsList.length &&
			!betSlip.freeBetLoading &&
			!!eventsCount,
		{ events, info, totalStake } = getBetslipData(data, betSlip, multipleOptions, minBetAmount),
		{ posWin, expOdds, expBonus, oddLimitExceededFlag } =
			betSlip.freeBet && freeBetAvailable && eventsCount > 1
				? { posWin: 0, expOdds: 1, expBonus: 0, oddLimitExceededFlag: false }
				: calculatePossibleWin(events, 0, loggedIn, currency, betSlip); // modifies events!

	posWin += calculatePossibleWinForUk(events, loggedIn, currency, betSlip, multipleOptions).posWin; // modifies events!

	let totalBonus = user.profile
			? (user.profile.bonus_balance || 0) +
			  (user.profile.bonus_win_balance || 0) +
			  (user.profile.frozen_balance || 0)
			: 0,
		insufficientBalance = user.profile && totalStake > user.profile.balance && totalStake > totalBonus,
		priceChangeNeeds2bConfirmed =
			(info.priceWentDown && betSlip.acceptPriceChanges !== BetPriceChangeEnum.AcceptAnyOdds) || // price went down and user is not accepting "any" changes
			(info.priceChange && betSlip.acceptPriceChanges === BetPriceChangeEnum.AlwaysAsk),
		betsCannotBePlaced =
			priceChangeNeeds2bConfirmed ||
			info.hasDeletedEvents ||
			info.hasEmptyStakes ||
			info.hasLockedEvents ||
			info.hasWrongStakes ||
			info.baseChange ||
			oddLimitExceededFlag ||
			(Config.betting.enableBankerBet && events.length - betSlip.bankerBetsCount < 2) ||
			(betSlip.type === BETSLIP_TYPE_SINGLE &&
				info.hasEventsFromSameGame &&
				Config.betting.blockSingleGameBets) ||
			(!loggedIn && !Config.betting.allowBetWithoutLogin) ||
			insufficientBalance ||
			!events.length, // betslip contains events that cannot be combined with others
		displayInfo = { warning: [], error: [], info: [] };
	freeBetAvailable &&
		betSlip.freeBet &&
		betSlip.selectedFreeBetAmount &&
		(totalStake = betSlip.selectedFreeBetAmount);

	!events.length && displayInfo.info.push(t("Betslip is empty. To select a bet, please click on any odd."));
	info.hasDeletedEvents && displayInfo.warning.push(t("Some events in betslip are not available at the moment."));
	priceChangeNeeds2bConfirmed && displayInfo.warning.push(t("Some events in betslip have changed price."));
	info.baseChange && displayInfo.warning.push(t("Some events in betslip have changed base."));
	info.hasConflicts &&
		betSlip.type !== BETSLIP_TYPE_SINGLE &&
		displayInfo.warning.push(t("Some events in betslip conflict with others."));
	(info.hasEmptyStakes || info.hasWrongStakes || (!betSlip.stake && betSlip.type !== BETSLIP_TYPE_SINGLE)) &&
		!(freeBetAvailable && betSlip.freeBet && betSlip.selectedFreeBetId) &&
		displayInfo.info.push(t("Set stake to place a bet."));
	insufficientBalance &&
		!(
			Config.main.betslipAdditionalMessages &&
			Config.main.betslipAdditionalMessages["insufficientBalance"]
		) &&
		displayInfo.warning.push(t("Insufficient balance"));
	oddLimitExceededFlag && displayInfo.warning.push(t("Odds limit exceeded."));
	// !loggedIn && displayInfo.warning.push(t("You need to login to be able to place a bet."));
	((betSlip.type === BETSLIP_TYPE_SYSTEM && events.length && events.length < 3) ||
		(betSlip.type === BETSLIP_TYPE_EXPRESS && events.length && events.length < 2) ||
		(betSlip.type === BETSLIP_TYPE_CHAIN && events.length && events.length < 2)) &&
		displayInfo.info.push(t("Add more events to place a bet."));
	return {
		events,
		info,
		posWin,
		expOdds,
		expBonus,
		freeBetAvailable,
		priceChangeNeeds2bConfirmed,
		betsCannotBePlaced,
		displayInfo,
		totalStake,
		insufficientBalance,
	};
});

export const getUkBetslipOptions = createSelector([events => events.length], function (count) {
	let all,
		total = 0;

	if (count === 2) {
		all = ukMultipleOptions.simple;
	} else {
		all = ukMultipleOptions.default.concat(getFolds(count) || []).concat(
			ukMultipleOptions[count] || [
				{
					type: 20,
					value: "Block",
				},
			]
		);
	}

	return Helpers.cloneDeep(all).map(option => {
		if (option.quantity) {
			total += option.quantity;
			return option;
		}
		if (option.size) {
			option.quantity = calculateSystemOptionsCount(option.size, count);
			option.size !== 1 && (total += option.quantity);
		} else {
			option.quantity = total;
		}
		return option;
	});
});

/**
 * @name createResetedBetslipEvent
 * @description Helper function to change and update existed betslip bets infos such as name, title, picked team name etc.
 * @param {object} game
 * @param {object} market
 * @param {object} event
 * @returns {object} updated game info
 * */
export const createResetedBetslipEvent = (game, market, event) => {
	return {
		title: `${game.team1_name} - ${game.team2_name || ""}`,
		pick: niceEventName(event.name, game),
		gameId: game.id,
		eventId: event.id,
		marketName: market.name,
	};
};

// export const maxCounterOffer = (price, oddsRaisedPercent) => {
//     return (price / (1 - oddsRaisedPercent * 0.01));
// };

export const counterOfferRounding = num => {
	let regEx = new RegExp("^-?\\d+(?:.\\d{0," + (Config.main.roundDecimalCoefficients || -1) + "})?"),
		price = num.toString().match(regEx)[0],
		lastElem = price.length - 1;

	if (price.charAt(lastElem) == 0) {
		return price.substring(0, lastElem);
	}
	return price;
};
